import classNames from 'classnames';
import Immutable from 'immutable';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import {RowType} from 'sharedreact/components/TableRow';
import type TableColumn from 'sharedreact/data/TableColumn';

type Props<T extends RowType> = {
  columns: Immutable.List<TableColumn<T>>,
  selectedColumns: Immutable.List<TableColumn<T>>,
  showDropdown: boolean,

  onToggleColumnSelection: (columnName: string) => void,
  onToggleDropdown: () => void
};

class TableColumnSelector<T extends RowType> extends React.PureComponent<Props<T>> {
  private dropdownElement: HTMLDivElement | null;
  private buttonElement: HTMLButtonElement | null;

  constructor (props: Props<T>) {
    super(props);

    this.dropdownElement = null;
    this.buttonElement = null;

    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
  }

  handleMouseDown (event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  }

  handleButtonClick (event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();

    this.props.onToggleDropdown();
  }

  handleDocumentClick (event: JQuery.ClickEvent<Document>) {
    const targetElement: HTMLElement = event.target;

    if ($(this.dropdownElement!).has(targetElement).length === 0) {
      if (this.props.showDropdown) {
        this.props.onToggleDropdown();
      }
    }
  }

  updateDropdownPosition () {
    const { showDropdown } = this.props;

    if (showDropdown && this.buttonElement != null && this.dropdownElement != null) {
      const refOffset = (($(this.buttonElement).offset()?.left ?? 0) + ($(this.buttonElement).outerWidth() ?? 0));

      const modalParent = $(this.buttonElement).closest('.modal-dialog');
      const slideOutLeftParent = $(this.buttonElement).closest('.esko_slideout.align-left');
      const slideOutRightParent = $(this.buttonElement).closest('.esko_slideout.align-right');
      
      let right: number = ($(window).width() ?? 0) - refOffset;
      let top: number = ($(this.buttonElement).offset()?.top ?? 0) + ($(this.buttonElement).outerHeight() ?? 0);
      if (modalParent[0] != null) {
        // It's a scrollTable inside a modal dialog - different offset calculation.
        right = modalParent[0].offsetLeft + modalParent[0].offsetWidth - refOffset;
        top = top - modalParent[0].offsetTop;
      } else if (slideOutLeftParent[0] != null) {
        // It's a scrollTable inside a left aligned slide-out - different offset calculation.
        right = slideOutLeftParent[0].offsetWidth - refOffset;
        top = top - slideOutLeftParent[0].offsetTop;
      } else if (slideOutRightParent[0] != null) {
        top = top - slideOutRightParent[0].offsetTop;
      }

      const maxHeight: number = ($(window).height() ?? 0) - top - 30;

      const dropdownMenuStyle = {
        position: 'fixed',
        top: top,
        right: right,
        maxHeight: maxHeight,
        overflowY: 'auto'
      };

      $(this.dropdownElement).find('.esko_dropdown-menu').css(dropdownMenuStyle);
    }
  }

  override componentDidMount (): void {
    this.updateDropdownPosition();

    $(document).click(this.handleDocumentClick);
  }

  override componentDidUpdate (): void {
    this.updateDropdownPosition();
  }

  override componentWillUnmount (): void {
    $(document).off('click', this.handleDocumentClick);
  }

  override render (): React.ReactNode {
    const { columns, selectedColumns, showDropdown, onToggleColumnSelection } = this.props;

    const id: string = _.uniqueId('dropdown-');

    const listItemComponents = columns.map((column, index) => {
      const handleItemClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        onToggleColumnSelection(column.name ?? '');
      };

      return (
        <li key={'colselection-' + index}>
          <a role='button' onClick={handleItemClick}>
            <input type='checkbox'
              checked={selectedColumns.findIndex(col => col.name != null && col.name === column.name) !== -1}
              readOnly
            />
            <span>{column.title}</span>
          </a>
        </li>
      );
    });

    return (
      <div className={classNames('esko_columnselector', 'esko_dropdown', {esko_open: showDropdown})}
        ref={(c) => { this.dropdownElement = c; }}
        onMouseDown={this.handleMouseDown}
      >
        <button className='btn esko_btn-icon esko_dropdown-toggle'
          type='button'
          id={id}
          aria-haspopup='true'
          aria-expanded={showDropdown ? 'true' : 'false'}
          onClick={this.handleButtonClick}
          ref={(c) => { this.buttonElement = c; }}
        >
          <span className='bi bi-three-dots-vertical' />
        </button>
        <ul className='esko_dropdown-menu esko_dropdown-menu-right' aria-labelledby={id}>
          {listItemComponents}
        </ul>
      </div>
    );
  }
}

export default TableColumnSelector;
